// src/features/admin/subCategoryApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  SubCategory,
  SubCategoryApiResponse,
  SubCategoryListParams,
  SubCategoryListByCatIdParams,
  SubCategoryCountParams,
  SubCategoryDetailParams,
  SubCategoryDeleteParams,
  AddSubCategoryRequest,
  UpdateSubCategoryRequest,
  SUB_CATEGORY_API_CONFIG,
} from '../../features/admin/types.ts';

export const subCategoryApi = createApi({
  reducerPath: 'subCategoryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers, { endpoint }) => {
      // Use admin_token instead of auth_token (based on your unitApi)
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      // Set authkey for all requests
      headers.set(
        'authkey',
        '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
      );
      
      if (endpoint === 'addSubCategory' || endpoint === 'updateSubCategory') {

      } else {
      
      
      }
      
      return headers;
    },
  }),
  tagTypes: ['SubCategory'],
  endpoints: (build) => ({
    // 1. List SubCategories
    listSubCategories: build.query<SubCategoryApiResponse<SubCategory[]>, SubCategoryListParams>({
      query: ({ page = 1, limit = 10, search_str = '' } = {}) => ({
        url: SUB_CATEGORY_API_CONFIG.ENDPOINTS.SUBCATEGORY_LIST,
        method: 'POST',
        body: {
          start: (page - 1) * limit,
          limit,
          ...(search_str && { search_str })
        },
      }),
      providesTags: ['SubCategory'],
      transformResponse: (response: any) => {
        console.log('List SubCategories Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.result || response.data || [],
          count: response.count || response.total || response.total_records || 0,
        };
      },
    }),

    // 2. Add SubCategory
    addSubCategory: build.mutation<SubCategoryApiResponse<any>, AddSubCategoryRequest>({
      query: (data) => {
        console.log('Add SubCategory Data:', data);
        
        const formData = new FormData();
        
        // Append required fields
        formData.append('cat_id', data.cat_id.toString());
        formData.append('sub_cat_name', data.sub_cat_name.trim());
        formData.append('sub_cat_short_name', data.sub_cat_short_name.trim());
        
        // Append optional fields only if they have values
        if (data.sub_cat_desc && data.sub_cat_desc.trim()) {
          formData.append('sub_cat_desc', data.sub_cat_desc.trim());
        }
        
        // Always append status, default to 1 if not provided
        formData.append('status', (data.status ?? 1).toString());
        
        // Append image file if provided
        if (data.sub_cat_main_image instanceof File) {
          formData.append('sub_cat_main_image', data.sub_cat_main_image);
        }

        // Debug: Log form data contents
        console.log('Add SubCategory FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        return {
          url: SUB_CATEGORY_API_CONFIG.ENDPOINTS.SUBCATEGORY_ADD,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['SubCategory'],
    }),

    // 3. Update SubCategory
    updateSubCategory: build.mutation<SubCategoryApiResponse<any>, UpdateSubCategoryRequest>({
      query: (data) => {
        console.log('Update SubCategory Data:', data);
        
        const formData = new FormData();
        
        // Append required fields
        formData.append('sub_cat_id', data.sub_cat_id.toString());
        formData.append('cat_id', data.cat_id.toString());
        formData.append('sub_cat_name', data.sub_cat_name.trim());
        formData.append('sub_cat_short_name', data.sub_cat_short_name.trim());
        
        // Append optional fields only if they have values
        if (data.sub_cat_desc && data.sub_cat_desc.trim()) {
          formData.append('sub_cat_desc', data.sub_cat_desc.trim());
        }
        
        // Always append status
        formData.append('status', (data.status ?? 1).toString());
        
        // Append image file if provided
        if (data.sub_cat_main_image instanceof File) {
          formData.append('sub_cat_main_image', data.sub_cat_main_image);
        }

        // Debug: Log form data contents
        console.log('Update SubCategory FormData entries:');
        for (let [key, value] of formData.entries()) {
          console.log(key, value);
        }

        return {
          url: SUB_CATEGORY_API_CONFIG.ENDPOINTS.SUBCATEGORY_UPDATE,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['SubCategory'],
    }),

    // 4. Delete SubCategory - Fixed: Using admin_token
    deleteSubCategory: build.mutation<SubCategoryApiResponse<any>, SubCategoryDeleteParams>({
      query: ({ sub_cat_id }) => {
        console.log('Delete SubCategory - sub_cat_id:', sub_cat_id);
        
        return {
          url: SUB_CATEGORY_API_CONFIG.ENDPOINTS.SUBCATEGORY_DELETE,
          method: 'POST',
          body: { sub_cat_id: sub_cat_id.toString() },
        };
      },
      invalidatesTags: ['SubCategory'],
      transformResponse: (response: any) => {
        console.log('Delete SubCategory Response:', response);
        return response;
      },
      transformErrorResponse: (response: any) => {
        console.error('Delete SubCategory Error Response:', response);
        return {
          status: response.status,
          data: response.data || { message: 'Unknown error occurred' }
        };
      },
    }),

    // 5. Get SubCategory Count
    getSubCategoryCount: build.query<SubCategoryApiResponse<{ count: number }>, SubCategoryCountParams>({
      query: ({ search_str = '' } = {}) => ({
        url: SUB_CATEGORY_API_CONFIG.ENDPOINTS.SUBCATEGORY_COUNT,
        method: 'POST',
        body: {
          ...(search_str && { search_str })
        },
      }),
      providesTags: ['SubCategory'],
      transformResponse: (response: any) => {
        console.log('SubCategory Count Response:', response);
        return response;
      },
    }),

    // 6. Get SubCategory Detail
    getSubCategoryDetail: build.query<SubCategoryApiResponse<SubCategory>, SubCategoryDetailParams>({
      query: ({ sub_cat_id }) => ({
        url: SUB_CATEGORY_API_CONFIG.ENDPOINTS.SUBCATEGORY_DETAIL,
        method: 'POST',
        body: { sub_cat_id: sub_cat_id.toString() },
      }),
      providesTags: ['SubCategory'],
    }),

    // 7. Get SubCategories by Category ID
    getSubCategoriesByCatId: build.query<SubCategoryApiResponse<SubCategory[]>, SubCategoryListByCatIdParams>({
      query: ({ cat_id }) => ({
        url: SUB_CATEGORY_API_CONFIG.ENDPOINTS.SUBCATEGORY_LIST_BY_CAT_ID,
        method: 'POST',
        body: { cat_id: cat_id.toString() },
      }),
      providesTags: ['SubCategory'],
      transformResponse: (response: any) => {
        console.log('SubCategories by Category ID Response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          result: response.result || response.data || [],
        };
      },
    }),
  }),
});

export const {
  useListSubCategoriesQuery,
  useAddSubCategoryMutation,
  useUpdateSubCategoryMutation,
  useDeleteSubCategoryMutation,
  useGetSubCategoryCountQuery,
  useGetSubCategoryDetailQuery,
  useGetSubCategoriesByCatIdQuery,
} = subCategoryApi;